"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import labels from "react-phone-number-input/locale/en.json";

import { cn } from "@/lib/utils";
import { useHydrated } from "@/hooks/use-hydrated";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const allCountries = getCountries().map((code) => ({
  code,
  name: labels[code] || code,
}));

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "ref"
> & {
  value?: string;
  onChange?: (value: string | undefined) => void;
  defaultCountry?: RPNInput.Country;
  country?: RPNInput.Country;
  countries?: RPNInput.Country[];
};

function PhoneInput({
  className,
  value,
  onChange,
  defaultCountry = "TZ",
  country,
  countries,
  ...props
}: PhoneInputProps) {
  const handleChange = (newValue: RPNInput.Value | undefined) => {
    onChange?.(newValue as string | undefined);
  };

  return (
    <RPNInput.default
      className={cn("flex", className)}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      countryCallingCodeEditable={false}
      inputComponent={PhoneInputField}
      international
      countries={countries}
      country={country}
      defaultCountry={country ?? defaultCountry}
      value={value as RPNInput.Value}
      onChange={handleChange}
      {...props}
    />
  );
}

function PhoneInputField({
  className,
  ...props
}: React.ComponentProps<"input">) {
  return (
    <input
      data-slot="phone-input"
      className={cn(
        "h-11 w-full min-w-0 rounded-md rounded-l-none border border-l-0 border-input bg-transparent px-3 py-1 text-base transition-[color,box-shadow] outline-none md:text-sm",
        "placeholder:text-muted-foreground/50",
        "",
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: { value: RPNInput.Country; label: string }[];
};

function CountrySelect({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);
  const mounted = useHydrated();

  const handleSelect = (country: RPNInput.Country) => {
    onChange(country);
    setOpen(false);
  };

  // Map options from react-phone-number-input to our format
  const countryList = React.useMemo(() => {
    if (options && options.length > 0) {
      return options.map((opt) => ({
        code: opt.value,
        name: opt.label || labels[opt.value] || opt.value,
      }));
    }
    return allCountries;
  }, [options]);

  // Defer Popover until after hydration to avoid Radix ID mismatch (aria-controls)
  if (!mounted) {
    return (
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={false}
        className="flex h-11 gap-2 rounded-r-none border-r-0 bg-transparent px-3 shadow-none dark:bg-transparent"
        disabled={disabled}
      >
        <FlagComponent country={value} countryName={value} />
        <ChevronDownIcon className="-mr-2 size-4 opacity-50" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex h-11 gap-2 rounded-r-none border-r-0 bg-transparent px-3 shadow-none dark:bg-transparent"
          disabled={disabled}
        >
          <FlagComponent country={value} countryName={value} />
          <ChevronDownIcon
            className={cn(
              "-mr-2 size-4 opacity-50 transition-transform",
              open && "rotate-180",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-0" side="bottom" align="start">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countryList.map((country) => (
                <CommandItem
                  key={country.code}
                  value={country.name}
                  onSelect={() => handleSelect(country.code)}
                >
                  <FlagComponent
                    country={country.code}
                    countryName={country.name}
                  />
                  <span className="flex-1 text-sm">{country.name}</span>
                  <span className="text-xs text-muted-foreground">
                    +{getCountryCallingCode(country.code)}
                  </span>
                  <CheckIcon
                    className={cn(
                      "size-4",
                      country.code === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FlagComponent({ country, countryName }: RPNInput.FlagProps) {
  const Flag = flags[country];

  return (
    <span className="flex overflow-hidden rounded-sm">
      {Flag && <Flag title={countryName} />}
    </span>
  );
}

export { PhoneInput };
